// ── gameStatesStore — batched Unabated gameState map ──────────────
//
// Polls hydra's /api/analytics/game-states for the union of visible
// dashboard gameKeys and stores the latest result. Consumers (the
// value dashboard's GameTable, marketStore.getGames for "final"
// override, etc.) read from this store instead of opening per-game
// SSE connections.
//
// Pattern: ValueDashboard owns the visible-key set + the polling
// effect; everyone else just reads via `useGameStateFor(gameKey)` or
// `useGameStatesStore.getState().getState(gameKey)` for non-React
// consumers.

import { create } from 'zustand';
import type { UnabatedGameState } from '../services/hydraAnalytics';

interface GameStatesStore {
  byGameKey: Map<string, UnabatedGameState | null>;
  /** Browser receipt time of the last complete fetch. Drives transport staleness. */
  lastFetchMs: number;
  /** Producer publication time. Never substituted for a source observation. */
  lastPublicationMs: number | null;
  /** Local time of the latest poll attempt, successful or failed. A failed
   *  poll updates this even when its error text is unchanged, so consumers
   *  can stop trusting an aged snapshot without running their own timers. */
  lastAttemptMs: number;
  /** Last fetch error (if any). Null when the most recent fetch succeeded. */
  error: string | null;
  /** Requested records rejected individually; their canonical state is unknown.
   * Explicit producer nulls are not validation failures. */
  invalidGameKeys: Set<string>;

  setStates: (states: Record<string, UnabatedGameState | null>, serverTs: number | null, receivedAtMs?: number, invalidKeys?: readonly string[]) => void;
  setError: (message: string | null, attemptedAtMs?: number) => void;
  /** Returns the latest known state for a gameKey, or null if hydra
   *  hasn't seen it yet (or the poller hasn't fetched it). */
  getState: (gameKey: string) => UnabatedGameState | null;
}

export const useGameStatesStore = create<GameStatesStore>((set, get) => ({
  byGameKey: new Map(),
  lastFetchMs: 0,
  lastPublicationMs: null,
  lastAttemptMs: 0,
  error: null,
  invalidGameKeys: new Set(),
  setStates: (states, serverTs, receivedAtMs = Date.now(), invalidKeys = []) => {
    // Replace the map wholesale — hydra returns the full requested set
    // every poll, so a key dropping out of the response means the
    // caller stopped subscribing to it. Keep a fresh Map identity so
    // Zustand subscribers see the change.
    const next = new Map<string, UnabatedGameState | null>();
    for (const [k, v] of Object.entries(states)) {
      next.set(k, v);
    }
    set({ byGameKey: next, lastFetchMs: receivedAtMs, lastPublicationMs: serverTs,
      lastAttemptMs: receivedAtMs, error: null, invalidGameKeys: new Set(invalidKeys) });
  },
  setError: (message, attemptedAtMs = Date.now()) =>
    set({ error: message, lastAttemptMs: attemptedAtMs }),
  getState: (gameKey) => get().byGameKey.get(gameKey) ?? null,
}));

/**
 * React hook for one game's state. Returns `null` until the poller
 * has fetched it (or until hydra reports the gameKey unknown).
 */
export function useGameStateFor(gameKey: string | null | undefined): UnabatedGameState | null {
  return useGameStatesStore((s) => (gameKey ? s.byGameKey.get(gameKey) ?? null : null));
}
